import React, { useMemo } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import { arrayOf, bool, func, string } from 'prop-types';
import { moneyBagIcon } from '@/assets/images';
import Avatar from '@/components/common/Avatar';
import CloudinaryUploadWidget from '@/components/common/CloudinaryUploadWidget';
import {
  DialogActionButton,
  DialogActions,
  DialogCancelButton,
  DialogHeader,
} from '@/components/common/CustomDialog';
import DeleteButton from '@/components/common/DeleteButton';
import CurrencyInput from '@/components/common/form/CurrencyInput';
import SectionsGenerator from '@/components/editDonor/SectionsGenerator';
import NumberFormatReturnedValues from '@/constants/ui/NumberFormatReturnedValues';
import useMobileView from '@/hooks/ui/useMobileView';
import { EDIT_COMPANY_INFO_CONFIG } from '@/lib/configs/form-configs';
import useCurrentUser from '@/lib/hooks/useCurrentUser';
import translate from '@/lib/intl/translate';
import { DonorType } from '@/propTypes';
import getInitials from '@/utils/getInitials';
import resizeCloudinaryImage from '@/utils/resizeCloudinaryImage';

function EditCompanyModal({
  data,
  errors,
  open,
  handleClose,
  handleSave,
  isLoading,
  setErrors,
  showErrors,
  error,
  updateData,
}) {
  const { avatar, majorDonorThreshold, orgName } = data;
  const { isSuperAdmin } = useCurrentUser();
  const isMobile = useMobileView();

  const initials = useMemo(() => getInitials(orgName), [orgName]);

  const OrgAvatar = (
    <Avatar
      alt="company-form-avatar"
      data-testid="company-form-avatar"
      size={isMobile ? '40px' : '64px'}
      src={avatar}
    >
      {initials}
    </Avatar>
  );

  const handleImageUpload = (e) => {
    updateData({ ...data, avatar: resizeCloudinaryImage(e.public_id, 128) });
  };

  const handleRemoveImage = () => {
    updateData({ ...data, avatar: null });
  };

  return (
    <Dialog
      aria-labelledby="edit-company-dialog"
      fullWidth
      maxWidth="md"
      onClose={handleClose}
      open={open}
      scroll="paper"
    >
      <DialogHeader
        headerText={translate('COMPANY_EDIT_PROFILE_LABEL')}
        onClose={handleClose}
      />
      <DialogContent dividers>
        <div className="relative pb-6 pr-6 pt-4">
          <div className="absolute left-3 top-4 md:left-8 md:top-2.5">
            {isSuperAdmin ? (
              <div className="relative">
                <CloudinaryUploadWidget onSuccess={handleImageUpload}>
                  {OrgAvatar}
                </CloudinaryUploadWidget>
                {avatar && (
                  <DeleteButton
                    onClick={handleRemoveImage}
                    className="absolute -right-1 -bottom-1 bg-white shadow-sm"
                    size="small"
                  />
                )}
              </div>
            ) : (
              OrgAvatar
            )}
          </div>
          <SectionsGenerator
            config={EDIT_COMPANY_INFO_CONFIG}
            data={data}
            errors={errors}
            setErrors={setErrors}
            showErrors={error}
            updateData={updateData}
          />
          {isSuperAdmin && (
            <div className="flex items-center">
              <div className="w-16 flex-shrink-0 sm:w-32">
                <img
                  src={moneyBagIcon}
                  alt=""
                  className="mx-auto h-4 w-4 md:h-6 md:w-6"
                />
              </div>
              <CurrencyInput
                amount={majorDonorThreshold / 100}
                returnedValue={NumberFormatReturnedValues.VALUE}
                setAmount={(val) => {
                  updateData({ ...data, majorDonorThreshold: val * 100 });
                }}
                label={translate('MAJOR_DONOR_THRESHOLD_LABEL')}
              />
            </div>
          )}
        </div>
      </DialogContent>
      <DialogActions>
        {Boolean(showErrors) && (
          <div className="px-2 text-error-main">
            {translate(
              error ? 'API_ERROR_UNKNOWN' : 'COMPANY_FORM_FIX_ERRORS_MESSAGE',
            )}
          </div>
        )}
        <DialogActionButton
          data-testid="edit-modal-save-button"
          onClick={handleSave}
          showLoader={isLoading}
        />

        <DialogCancelButton
          data-testid="edit-modal-cancel-button"
          onClick={handleClose}
        />
      </DialogActions>
    </Dialog>
  );
}

EditCompanyModal.propTypes = {
  data: DonorType.isRequired,
  errors: arrayOf(string),
  isLoading: bool,
  open: bool,
  handleClose: func,
  handleSave: func,
  error: bool,
  setErrors: func,
  showErrors: bool,
  updateData: func,
};

EditCompanyModal.defaultProps = {
  errors: [],
  open: false,
  handleClose: () => {},
  handleSave: () => {},
  isLoading: false,
  setErrors: () => {},
  showErrors: false,
  error: false,
  updateData: () => {},
};

export default EditCompanyModal;
